import { Input, Tooltip, Button, Breadcrumb, Layout, theme, Space } from 'antd';
import { Outlet, Link } from 'react-router-dom';
import { SettingOutlined, QuestionCircleOutlined, PlusOutlined, FileTextOutlined, DeleteOutlined } from '@ant-design/icons';
import HelpModal from './components/HelpModal';
import SettingsModal from './components/SettingsModal';
import CreateTaskModal from './components/CreateTaskModal';
import MyTgAvatar from './components/MyTgAvatar';
import BoardFilters from './components/BoardFilters';

import type { RootState } from './store';
import type { InputRef } from 'antd';

import { useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { setSearch } from './store/settings';
import { useGetTodoByIdQuery, useDeleteTodoReportMutation } from './store/todos';
import useEventListener from './hooks/useEventListener';
import useBoolean from './hooks/useBoolean';
import { useConfirm } from './hooks/useConfirm';
import TodosSider from './components/TodosSider';
import { resolvePublicUrl } from './utils/publicUrl';

const MyLayout = () => {
    const dispatch = useDispatch();
    const { token } = theme.useToken();
    const settings = useSelector((state: RootState) => state.settings);
    const searchRef = useRef<InputRef>(null);
    const { confirm, confirmModal } = useConfirm();
    const [deleteTodoReport, { isLoading: isDeletingReport }] = useDeleteTodoReportMutation();

    const isOpenHelpModal = useBoolean();
    const isOpenSettingsModal = useBoolean();
    const isOpenCreateTaskModal = useBoolean();

    const { projectUrl, todoId } = useParams();
    const { data: activeTodo } = useGetTodoByIdQuery(
        { todoId: Number(todoId) },
        { skip: !todoId },
    );

    useEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
            searchRef.current?.blur();
        }

        const activeElement = document.activeElement;
        const isInputElement = activeElement && (
            activeElement.tagName === 'INPUT'
                || activeElement.tagName === 'TEXTAREA'
                || activeElement.getAttribute('contenteditable'));

        if (isInputElement) {
            return;
        }

        if ((event.key === 'f' || event.key === 'а') && event.ctrlKey) {
            event.preventDefault();
            event.stopPropagation();
            searchRef.current?.focus();
        }
        if ((event.key === 'o' || event.key === 'щ') && event.ctrlKey) {
            event.preventDefault();
            event.stopPropagation();
            if (todoId) {
                isOpenCreateTaskModal.set(true);
            }
        }
    });

    const handleSearchChange = (input: string) => {
        dispatch(setSearch(input));
    };

    const bread = [
        {
            title: (
                <Link to="/projects" style={{ color: token.colorText }}>
                    Проекты
                </Link>
            ),
        },
    ];

    if (projectUrl) {
        bread.push({
            title: (
                <Link to={`/projects/${projectUrl}`} style={{ color: token.colorText }}>
                    {projectUrl}
                </Link>
            ),
        });
    }

    if (todoId) {
        const epicName = activeTodo?.todoName?.trim() || `Эпик ${todoId}`;
        bread.push({
            title: (
                <Tooltip title={epicName}>
                    <span className="app-breadcrumb-epic" style={{ color: token.colorText }}>
                        {epicName}
                    </span>
                </Tooltip>
            ),
        });
    }

    const mainClassName = [
        'app-shell__main',
        settings.theme?.backgroundImage ? 'app-shell__main--themed' : '',
        settings.theme?.isDark ? 'app-shell__main--dark' : '',
    ].filter(Boolean).join(' ');

    const radius = settings.theme?.borderRadius ?? token.borderRadius;
    const fontSize = settings.theme?.fontSize ?? token.fontSize;

    const shellStyle: React.CSSProperties = {
        ['--app-radius' as string]: `${radius}px`,
        ['--app-radius-sm' as string]: `${Math.max(0, radius - 2)}px`,
        ['--app-font-size' as string]: `${fontSize}px`,
        ['--app-primary' as string]: token.colorPrimary,
        ['--app-card-title-weight' as string]: settings.theme?.regularCardTitles ? '400' : '600',
        fontSize,
    };

    const mainStyle: React.CSSProperties = {
        ...shellStyle,
        ...(settings.theme?.backgroundImage
            ? { backgroundImage: `url(${settings.theme.backgroundImage})` }
            : {}),
    };

    const handleDeleteHtmlReport = async () => {
        if (!todoId || !projectUrl || !activeTodo?.reportPath) {
            return;
        }
        const epicName = activeTodo.todoName?.trim() || `Эпик ${todoId}`;
        const ok = await confirm({
            title: 'Удалить HTML отчёт',
            message: `Удалить HTML-отчёт у эпика «${epicName}»?`,
            okText: 'Удалить',
        });
        if (!ok) {
            return;
        }
        await deleteTodoReport({
            todoId: Number(todoId),
            projectUrl,
        });
    };

    return (
        <Layout className="app-shell" style={shellStyle}>
            {projectUrl &&
                <TodosSider
                    activeTodoId={todoId ? +todoId : undefined}
                    projectUrl={projectUrl}
                />
            }

            <Layout className={mainClassName} style={mainStyle}>
                <div className="app-topbar">
                    <div className="app-topbar__left">
                        <Breadcrumb
                            items={bread}
                            separator={<span style={{ color: token.colorText }}>/</span>}
                            style={{ color: token.colorText }}
                        />
                        {todoId && activeTodo?.reportPath ? (
                            <Space size={4} className="app-breadcrumb-report" align="center">
                                <Button
                                    type="primary"
                                    icon={<FileTextOutlined />}
                                    onClick={() => {
                                        const url = resolvePublicUrl(activeTodo.reportPath);
                                        if (url) {
                                            window.open(url, '_blank', 'noopener,noreferrer');
                                        }
                                    }}
                                >
                                    HTML отчет
                                </Button>
                                <Tooltip title="Удалить HTML отчёт">
                                    <Button
                                        type="text"
                                        danger
                                        className="app-breadcrumb-report-delete"
                                        icon={<DeleteOutlined />}
                                        loading={isDeletingReport}
                                        onClick={handleDeleteHtmlReport}
                                        aria-label="Удалить HTML отчёт"
                                    />
                                </Tooltip>
                            </Space>
                        ) : null}
                    </div>

                    <div className="app-topbar__center">
                        <div className="app-topbar__search">
                            <Input.Search
                                allowClear
                                placeholder="Поиск…  Ctrl+F"
                                value={settings.search}
                                onChange={(e) => handleSearchChange(e.target.value)}
                                ref={searchRef}
                                styles={{
                                    input: { color: token.colorText },
                                }}
                            />
                        </div>
                    </div>

                    <div className="app-topbar__right">
                        {todoId && <BoardFilters />}

                        <Tooltip title="Справка">
                            <Button
                                icon={<QuestionCircleOutlined style={{ color: token.colorText, fontSize: 16 }} />}
                                type="text"
                                style={{ color: token.colorText }}
                                onClick={() => isOpenHelpModal.set(true)}
                            />
                        </Tooltip>

                        <Tooltip title="Настройки">
                            <Button
                                icon={<SettingOutlined style={{ color: token.colorText, fontSize: 16 }} />}
                                type="text"
                                style={{ color: token.colorText }}
                                onClick={() => isOpenSettingsModal.set(true)}
                            />
                        </Tooltip>

                        {todoId &&
                            <Button
                                type="primary"
                                icon={<PlusOutlined />}
                                onClick={() => isOpenCreateTaskModal.set(true)}
                            >
                                Задача
                            </Button>
                        }

                        <Link to="/projects">
                            <MyTgAvatar />
                        </Link>
                    </div>
                </div>

                <Layout.Content
                    className="app-shell__content"
                    style={{ height: '100%', position: 'relative', minHeight: 0, minWidth: 0 }}
                >
                    <Outlet />
                </Layout.Content>
            </Layout>

            <HelpModal
                open={isOpenHelpModal.value}
                onClose={() => isOpenHelpModal.set(false)}
            />

            <SettingsModal
                open={isOpenSettingsModal.value}
                onClose={() => isOpenSettingsModal.set(false)}
            />

            {todoId && (
                <CreateTaskModal
                    open={isOpenCreateTaskModal.value}
                    todoId={+todoId}
                    onClose={() => isOpenCreateTaskModal.set(false)}
                />
            )}
            {confirmModal}
        </Layout>
    );
};

export default MyLayout;
